import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { getListingsById } from '../../../ducks/marketplaceData.duck';
import {
  fetchRacingThemeCmsEntities,
  selectRacingThemeCmsPage,
} from '../../../ducks/racingThemeCms.duck';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { pageHasRacingThemeSections, resolveRacingThemeSections } from './index';

const { UUID } = sdkTypes;

const toUuids = ids => (ids || []).map(id => new UUID(id));

/**
 * Resolve Console page data into Racing Theme sectionTypes + racingProps.
 * Also ensures entity fetch has been triggered for the page.
 *
 * @param {string} pageId
 * @param {Object|null|undefined} pageData
 * @returns {Object|null|undefined}
 */
export const useResolvedRacingThemePageData = (pageId, pageData) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const cmsPage = useSelector(state => selectRacingThemeCmsPage(state, pageId));
  const listings = useSelector(state => getListingsById(state, toUuids(cmsPage.listingIds)));

  const hasRacingSections = pageHasRacingThemeSections(pageData?.sections);
  const listingImageConfig = config?.layout?.listingImage;

  useEffect(() => {
    if (!pageId || !hasRacingSections) {
      return;
    }
    if (cmsPage.fetched || cmsPage.inProgress) {
      return;
    }
    dispatch(
      fetchRacingThemeCmsEntities({
        pageId,
        sections: pageData.sections,
        listingImageConfig: listingImageConfig || {},
      })
    );
  }, [
    dispatch,
    pageId,
    hasRacingSections,
    cmsPage.fetched,
    cmsPage.inProgress,
    pageData?.sections,
    listingImageConfig,
  ]);

  const listingsById = useMemo(() => {
    return (listings || []).reduce((acc, listing) => {
      const id = listing?.id?.uuid;
      if (id) {
        acc[id] = listing;
      }
      return acc;
    }, {});
  }, [listings]);

  const profilesById = useMemo(() => {
    return (cmsPage.profiles || []).reduce((acc, profile) => {
      if (profile?.id) {
        acc[profile.id] = profile;
      }
      return acc;
    }, {});
  }, [cmsPage.profiles]);

  return useMemo(() => {
    if (!hasRacingSections) {
      return pageData;
    }
    const sections =
      cmsPage.fetched && cmsPage.resolvedSections != null
        ? cmsPage.resolvedSections
        : pageData?.sections;
    return resolveRacingThemeSections(
      { ...pageData, sections },
      {
        listingsById,
        profilesById,
        config,
        intl,
        routeConfiguration,
      }
    );
  }, [
    hasRacingSections,
    pageData,
    listingsById,
    profilesById,
    cmsPage.fetched,
    cmsPage.resolvedSections,
    config,
    intl,
    routeConfiguration,
  ]);
};
